import { FC } from 'react'
import styled from 'styled-components'

import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import Button from 'shared/components/Button'
import { ProductQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts } from 'shared/lib'
import { ISizePickerOptionVariant } from 'shared/types/ISizePickerOption'

const TP = 'productTemplate.components.productPicker.BuyButtonRow'

const getISPrice = (optionVariant?: ISizePickerOptionVariant) => {
  if (!optionVariant || !optionVariant?.instantShipLowestPriceCents?.display) {
    return null
  }
  return optionVariant.instantShipLowestPriceCents.display
}

const getOptionVariantPrice = (optionVariant?: ISizePickerOptionVariant) => {
  if (!optionVariant) {
    return null
  }
  if (
    optionVariant?.lowestPriceCents?.localizedValue <
    (optionVariant?.instantShipLowestPriceCents?.localizedValue || Infinity)
  ) {
    return optionVariant.lowestPriceCents.display
  }
  return optionVariant?.instantShipLowestPriceCents?.display
}

const getButtonText = (
  isMarketPricing: boolean,
  isHideFastestToYou: boolean,
  isApparel: boolean,
  optionVariant?: ISizePickerOptionVariant,
) => {
  let selectedOptionVariantPrice = null

  if (isMarketPricing) {
    if (isHideFastestToYou) {
      selectedOptionVariantPrice = optionVariant?.lowestPriceCents?.localizedValue
        ? optionVariant?.lowestPriceCents?.display
        : null
    } else {
      selectedOptionVariantPrice = getOptionVariantPrice(optionVariant)
    }
  } else {
    selectedOptionVariantPrice = getISPrice(optionVariant)
  }

  if (selectedOptionVariantPrice) {
    if (isApparel) {
      // remove decimals
      return `${selectedOptionVariantPrice}`.replace(/\.\d\d$/, '')
    }
    // remove decimals and add + sign
    return `${selectedOptionVariantPrice}`.replace(/\.\d\d$/, '') + '+'
  }
  return t(`${TP}.outOfStock`, 'Out of stock')
}

const BuyButtonRow: FC = () => {
  const { isApparel, selectedSize, setActiveProductModal } = useProductTemplateContext()
  const isMarketPricing = useFeatureFlag(FeatureFlag.MARKET_PRICING)
  const isHideFastestToYou = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)

  return (
    <BuyButtonsWrapper $isApparel={isApparel}>
      <div>
        <SizingLabel data-qa={ProductQA.BuyNewSizingLabel}>
          {t(`${TP}.buyNew`, 'Buy New')}
        </SizingLabel>
        <Button
          buttonType="primary2"
          $fill
          disabled={!selectedSize?.new}
          onClick={() => setActiveProductModal('new')}
          qaAttr={`${ProductQA.BuyNew}BuyNowButton`}
          data-testid="buy-new-button"
        >
          {getButtonText(isMarketPricing, isHideFastestToYou, isApparel, selectedSize?.new)}
        </Button>
      </div>
      {!isApparel && (
        <div>
          <SizingLabel data-qa={ProductQA.BuyUsedSizingLabel}>
            {t(`${TP}.buyUsed`, 'Buy Used')}
          </SizingLabel>
          <Button
            buttonType="primary1"
            $fill
            disabled={!selectedSize?.used}
            onClick={() => setActiveProductModal('used')}
            qaAttr={`${ProductQA.BuyUsed}BuyNowButton`}
            data-testid="buy-used-button"
          >
            {getButtonText(isMarketPricing, isHideFastestToYou, isApparel, selectedSize?.used)}
          </Button>
        </div>
      )}
    </BuyButtonsWrapper>
  )
}

const BuyButtonsWrapper = styled.div<{ $isApparel: boolean }>`
  column-gap: 11px;
  display: grid;
  grid-template-columns: ${({ $isApparel }) => (!$isApparel ? '1fr 1fr' : '1fr')};
  margin-top: 30px;
`

const SizingLabel = styled.label`
  ${fonts.SUBTITLE_2};
  color: ${colors.FC2_BLACK};
  display: block;
  margin-bottom: 20px;
`

export default BuyButtonRow
